import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import {
  type APIResponse,
  type Contact,
  type ContactAddress,
  type ContactEmail,
  type ContactFax,
  type ContactNote,
  type ContactPhone,
  type ContactTagConnection,
  type ContactWebsite,
} from "@/lib/types";

import {
  searchV4,
  emptySearchResponse,
  type SearchRequest,
} from "@/lib/data/requests/search/search";

export const baseContactRoute = "contacts/v2";

export type DuplicateMatchReason =
  | "exactName"
  | "similarName"
  | "email"
  | "phone"
  | "address";

export type PossibleDuplicateContact = {
  id: string;
  type: "individual" | "organization" | "group";
  individualFirstName: string | null;
  individualMiddleName: string | null;
  individualLastName: string | null;
  organizationName: string | null;
  groupName: string | null;
  matchedOn: DuplicateMatchReason[];
} & Partial<Pick<Contact, "phones" | "addresses">>;

export type CreateContactResponse =
  | APIResponse<Contact>
  | APIResponse<PossibleDuplicateContact[]>;

export const isPossibleDuplicateResponse = (
  response: CreateContactResponse,
): response is APIResponse<PossibleDuplicateContact[]> =>
  Array.isArray(response.data);

export type ContactSearchFilters = {
  context?: "matterContactPicker";
  contactIds?: string[];
  excludeContactIds?: string[];
  matterIds?: string[];
  addresses?: {
    city?: string[];
    state?: string[];
    street1?: string[];
    zipCode?: string[];
  };
  emails?: { address?: string[] };
  faxes?: { number?: string[] };
  individualSubTypes?: string[];
  name?: string[];
  organizationSubTypes?: string[];
  order?: "asc" | "desc";
  pageNumber?: number;
  pageSize?: number;
  phones?: { number?: string[] };
  preferredContactMethod?: string[];
  search?: string | string[];
  sort?: string;
  tagConnections?: { label?: string[] };
  title?: string[];
  type?: string[];
  websites?: { address?: string[] };
};

type SearchContactsPagination = {
  count?: number;
  pageNumber?: number;
  pageSize?: number;
};

const DEFAULT_CONTACT_SEARCH_PAGE_SIZE = 100;

export function retrieveContactV1(id?: string) {
  return authenticatedFastApiAxiosInstance
    .get<APIResponse<Contact>>(`${baseContactRoute}/${id}`)
    .then((res) => res.data);
}

type CreateOrUpdateContact = {
  phones?: Pick<
    ContactPhone,
    "extension" | "description" | "isPrimary" | "label" | "number"
  >[];
  emails?: Pick<
    ContactEmail,
    "isPrimary" | "label" | "address" | "description"
  >[];
  addresses?: Pick<
    ContactAddress,
    | "city"
    | "country"
    | "county"
    | "description"
    | "isPrimary"
    | "label"
    | "state"
    | "street1"
    | "street2"
    | "zipCode"
  >[];
  faxes?: Pick<
    ContactFax,
    "number" | "extension" | "isPrimary" | "label" | "description"
  >[];
  websites?: Pick<ContactWebsite, "address" | "label" | "description">[];
  notes?: Pick<ContactNote, "message" | "draft">[];
  tagConnections?: Pick<ContactTagConnection, "tagId">[];
} & Partial<
  Omit<
    Contact,
    | "phones"
    | "emails"
    | "addresses"
    | "faxes"
    | "websites"
    | "notes"
    | "tagConnections"
  >
>;

export function createContactV1(
  data: Pick<Contact, "type"> &
    CreateOrUpdateContact & {
      createDespitePossibleDuplicate?: boolean;
    },
) {
  return authenticatedFastApiAxiosInstance
    .post<CreateContactResponse>(baseContactRoute, data)
    .then((res) => res.data);
}

export function updateContactV1(
  data: Pick<Contact, "id" | "dateModified"> & Partial<Contact>,
) {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<Contact>>(baseContactRoute, data)
    .then((res) => res.data);
}

export const buildContactSearchRequest = (
  data: ContactSearchFilters,
  view: "list" | "count" = "list",
): SearchRequest => {
  const { search, context, pageNumber, pageSize, sort, order, ...filters } =
    data;
  const query =
    (Array.isArray(search) ? search.join(" ") : search)?.trim() || undefined;
  return {
    entityType: "contact",
    view,
    query,
    filters,
    context:
      context ??
      (filters.matterIds?.length ? "matterContactPicker" : undefined),
    ...(view === "list"
      ? {
          pageNumber,
          pageSize: pageSize ?? DEFAULT_CONTACT_SEARCH_PAGE_SIZE,
          sort,
          order,
        }
      : {}),
  };
};

export async function searchContactsV3(
  data: ContactSearchFilters,
  signal?: AbortSignal,
  view: "list" | "count" = "list",
) {
  // Explicitly empty ID scopes must never become an unfiltered directory query.
  if (data.contactIds?.length === 0 || data.matterIds?.length === 0) {
    return emptySearchResponse<Contact>(data.pageNumber, data.pageSize);
  }
  return searchV4<Contact>(buildContactSearchRequest(data, view), signal);
}

export async function searchContactIds(
  data: ContactSearchFilters,
  signal?: AbortSignal,
) {
  const contacts: Contact[] = [];
  let pageNumber: number | undefined = 1;
  let response: APIResponse<Contact[]>;
  do {
    response = await searchContactsV3(
      { ...data, pageNumber, pageSize: 500 },
      signal,
    );
    contacts.push(...response.data);
    pageNumber = getSearchContactsNextPageNumber(response);
  } while (pageNumber);
  return { ...response, data: contacts };
}

const getPagination = (response: APIResponse<Contact[]>) =>
  response.pagination as SearchContactsPagination | undefined;

export const getSearchContactsNextPageNumber = (
  response: APIResponse<Contact[]> | undefined,
) => {
  if (!response) return undefined;
  const { count, pageNumber, pageSize } = getPagination(response) ?? {};
  if (count == null || pageNumber == null || pageSize == null) {
    return undefined;
  }
  return pageNumber * pageSize < count ? pageNumber + 1 : undefined;
};

export const getSearchContactsCount = (
  response: APIResponse<Contact[]> | undefined,
) => (response ? getPagination(response)?.count : undefined);

export function deleteContactV1({ id }: { id: string }) {
  return authenticatedFastApiAxiosInstance
    .delete<APIResponse<Contact>>(`${baseContactRoute}/${id}`)
    .then((res) => res.data);
}
